import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './estilodashuser.css';

function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR');
}

function formatMesReferencia(value) {
  if (!value || !String(value).includes('-')) return value || '—';
  const [ano, mes] = String(value).split('-');
  return `${mes}/${ano}`;
}

function getStatusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'pago') return 'badge badge-ativo';
  if (s === 'isento') return 'badge badge-isento';
  if (s === 'cancelado') return 'badge badge-inativo';
  return 'badge badge-normal';
}

export default function ListarPagamentos({ userId }) {
  const [pagamentos, setPagamentos] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (userId) carregar();
  }, [userId]);

  const carregar = async () => {
    try {
      setLoading(true);
      setErro('');

      const [respPag, respAss] = await Promise.all([
        api.get('/api/meus-pagamentos'),
        api.get('/api/minhas-assinaturas'),
      ]);

      setPagamentos(respPag.data || []);
      setAssinaturas(respAss.data || []);
    } catch (err) {
      console.error('Erro ao carregar pagamentos do cliente:', err);
      setErro('Erro ao carregar seus pagamentos.');
    } finally {
      setLoading(false);
    }
  };

  const resumoFinanceiro = useMemo(() => {
    const pagos = pagamentos.filter((p) => String(p.status || '').toLowerCase() === 'pago');
    const abertos = pagamentos.filter((p) =>
      ['aguardando pagamento', 'em aberto', 'pendente'].includes(String(p.status || '').toLowerCase())
    );
    const totalPago = pagos.reduce((acc, p) => acc + Number(p.valor || p.valor_calculado || 0), 0);
    const totalAberto = abertos.reduce((acc, p) => acc + Number(p.valor_calculado || p.valor || 0), 0);

    return {
      total: pagamentos.length,
      pagos: pagos.length,
      emAberto: abertos.length,
      totalPago,
      totalAberto,
    };
  }, [pagamentos]);

  const linhas = useMemo(() => {
    return pagamentos
      .map((p) => {
        const assinatura =
          assinaturas.find((a) => Number(a.id) === Number(p.assinatura_id)) || null;

        return {
          ...p,
          assinatura,
        };
      })
      .sort((a, b) => {
        const mesA = String(a.mes_referencia || '');
        const mesB = String(b.mes_referencia || '');
        if (mesA !== mesB) return mesB.localeCompare(mesA);

        const dataA = new Date(a.data_pagamento || a.created_on || 0).getTime();
        const dataB = new Date(b.data_pagamento || b.created_on || 0).getTime();
        return dataB - dataA;
      });
  }, [pagamentos, assinaturas]);

  return (
    <div className="user-payments-wrap">
      <div className="user-payments-card">
        <div className="user-payments-header">
          <h3>Meus Pagamentos</h3>
          <button type="button" className="lic-btn-outline" onClick={carregar}>
            Recarregar
          </button>
        </div>

        <div className="cliente-help-box" style={{ marginBottom: 14 }}>
          <strong>Como ler esta tela:</strong> aqui você confere o histórico de cobranças da sua conta, com o mês de referência,
          valor, status e a descrição do que foi cobrado. Use esta área para validar se o lançamento corresponde ao que está contratado.
        </div>

        {erro && <div className="alert-box alert-error">{erro}</div>}

        <div className="lic-kpis lic-kpis-compact">
          <div className="lic-kpi">
            <div className="lic-kpi-label">Lançamentos</div>
            <div className="lic-kpi-value">{resumoFinanceiro.total}</div>
          </div>
          <div className="lic-kpi">
            <div className="lic-kpi-label">Pagos</div>
            <div className="lic-kpi-value">{resumoFinanceiro.pagos}</div>
          </div>
          <div className="lic-kpi">
            <div className="lic-kpi-label">Em aberto</div>
            <div className="lic-kpi-value">{resumoFinanceiro.emAberto}</div>
          </div>
          <div className="lic-kpi">
            <div className="lic-kpi-label">Total pago</div>
            <div className="lic-kpi-value">R$ {formatMoney(resumoFinanceiro.totalPago)}</div>
          </div>
        </div>

        {loading ? (
          <p className="lic-loading">Carregando pagamentos...</p>
        ) : (
          <div className="payments-list">
            {linhas.length === 0 ? (
              <div className="cliente-empty-box">
                Nenhum pagamento foi encontrado na sua conta até o momento.
              </div>
            ) : (
              linhas.map((p) => (
                <div key={p.id} className="payment-item-card">
                  <div className="payment-item-top">
                    <div>
                      <div className="payment-item-title">
                        {p.assinatura?.nome_plano || 'Cobrança da conta'}
                      </div>
                      <div className="payment-item-subtitle">
                        Mês de referência: <strong>{formatMesReferencia(p.mes_referencia)}</strong>
                      </div>
                    </div>

                    <span className={getStatusBadgeClass(p.status)}>
                      {p.status || '—'}
                    </span>
                  </div>

                  <div className="payment-item-grid">
                    <div className="payment-item-line">
                      <span>Valor</span>
                      <strong>R$ {formatMoney(p.valor || p.valor_calculado || 0)}</strong>
                    </div>

                    <div className="payment-item-line">
                      <span>Data</span>
                      <strong>{formatDate(p.data_pagamento)}</strong>
                    </div>

                    <div className="payment-item-line">
                      <span>Método</span>
                      <strong>{p.metodo_pagamento || '—'}</strong>
                    </div>

                    <div className="payment-item-line">
                      <span>Origem</span>
                      <strong>{p.origem_cobranca || '—'}</strong>
                    </div>
                  </div>

                  <div className="payment-description-box">
                    <div className="payment-description-title">Descrição</div>
                    <div className="payment-description-text">
                      {p.descricao || 'Sem descrição informada.'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="lic-footnote">
          Caso exista divergência entre o valor cobrado e sua contratação, entre em contato com o suporte informando o mês de referência e a descrição exibida.
        </div>
      </div>
    </div>
  );
}
